import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  postCall,
  deleteCall,
  getById,
} from "../../utils/api";
import toast from "react-hot-toast";

const initialState = {
  data: {
    data: [], // array of offers
    pagination: null, // optional, in case response includes pagination
  },
  offerDetail: null,
  error: null,
  status: "idle",
  isUpdateStatus: "idle",
};

export const getSelectedOffers = createAsyncThunk(
  "selectedOffers/get",
  async ({ payload, page = 1, per_page = 50 }) => {
    try {
      const response = await postCall(
        `/admin/v1/selected-offers-v1.0.23/list?page=${page}&per_page=${per_page}`,
        payload,
        false
      );
      return response;
    } catch (error) {
      return Promise?.reject(error);
    }
  }
);

export const getSelectedOffersById = createAsyncThunk(
  "selectedOffers/getById",
  async (id) => {
    try {
      const response = await getById(`/admin/v1/selected-offers-v1.0.23`, id, false);
      return response;
    } catch (error) {
      toast.error(error?.response?.data.message);
      return Promise.reject(error);
    }
  }
);

export const addSelectedOffers = createAsyncThunk(
  "selectedOffers/add",
  async (formData) => {
    try {
      const response = await postCall("/admin/v1/selected-offers-v1.0.23", formData);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

export const updateSelectedOffers = createAsyncThunk(
  "selectedOffers/update",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const response = await postCall(`/admin/v1/selected-offers-v1.0.23/${id}`, payload);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteSelectedOffers = createAsyncThunk(
  "selectedOffers/delete",
  async (id) => {
    try {
      await deleteCall(`/admin/v1/selected-offers-v1.0.23/${id}`);
      return id;
    } catch (error) {
      toast.error(error?.response?.data.message);
      return Promise.reject(error);
    }
  }
);

export const dragDropSelectedOffers = createAsyncThunk(
  "selectedOffers/dragDrop",
  async ({ id, to }) => {
    try {
      const response = await postCall(
        `/admin/v1/selected-offers/${id}/direction`,
        {
          to,
        }
      );
      return response;
    } catch (error) {
      toast.error(error?.response?.data.message);
      return Promise.reject(error);
    }
  }
);

const SelectedOffersSlice = createSlice({
  name: "selectedOffers",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Get All
      .addCase(getSelectedOffers.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getSelectedOffers.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = action.payload;
      })
      .addCase(getSelectedOffers.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to fetch data.";
      })

      // Get by ID
      .addCase(getSelectedOffersById.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getSelectedOffersById.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.offerDetail = action.payload;
      })
      .addCase(getSelectedOffersById.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to fetch detail.";
      })

      // Add
      .addCase(addSelectedOffers.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(addSelectedOffers.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data.data.push(action.payload);
      })
      .addCase(addSelectedOffers.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to add item.";
      })

      // Update
      .addCase(updateSelectedOffers.pending, (state) => {
        state.isUpdateStatus = "loading";
        state.error = null;
      })
      .addCase(updateSelectedOffers.fulfilled, (state, action) => {
        state.isUpdateStatus = "succeeded";
        const index = state.data.data.findIndex(
          (item) => item.id === action.payload.data.id
        );
        if (index !== -1) {
          state.data.data[index] = action.payload.data;
        }
      })
      .addCase(updateSelectedOffers.rejected, (state, action) => {
        state.isUpdateStatus = "failed";
        state.error = action.payload || "Failed to update item.";
      })

      // Delete
      .addCase(deleteSelectedOffers.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(deleteSelectedOffers.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data.data = state.data.data.filter(
          (item) => item.id !== action.payload
        );
      })
      .addCase(deleteSelectedOffers.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to delete item.";
      })

      // Drag and Drop
      .addCase(dragDropSelectedOffers.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(dragDropSelectedOffers.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data.data = action.payload.data;
      })
      .addCase(dragDropSelectedOffers.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to drag and drop item.";
      });
  },
});

export default SelectedOffersSlice.reducer;
